import type { PvpHullBlocker } from '@kenocyber/game-domain/pvp';
import {
  PVP_BASE_SPEED_BPS,
  PVP_MAX_CATCHUP_MS,
  PVP_SHIP_RADIUS_MU,
  PVP_SHIP_SPEED_MU_PER_TICK,
  pvpAdvanceShip,
  pvpMaxTravelMu,
  pvpTickTravelMu,
} from '@kenocyber/game-domain/pvp';
import type { PvpIntent } from './connection';

/**
 * Where the player's own ship is DRAWN, between one authoritative frame and the
 * next.
 *
 * ## The problem this exists to solve
 *
 * The room ticks at 20 Hz and a browser draws at 60 or more. Drawing the local
 * ship straight from authoritative samples means its position changes only
 * every 50 ms: measured on a real recording, 19% of frames at 30 fps showed
 * zero movement and the largest single-frame jump was 20.6 px. The player
 * described it as teleporting, and they were describing exactly what was
 * happening.
 *
 * Interpolating instead - which is right for the OPPONENT - would be worse
 * here, because it adds delay to the one ship whose input the player can feel.
 *
 * ## What this does
 *
 * It predicts. Every intent the client sends is kept with its sequence number;
 * the predicted position is the last acknowledged authoritative position with
 * every unacknowledged intent replayed on top, using
 * `pvpAdvanceShip` - the SAME function the room runs, not a copy of it.
 *
 * When a frame arrives the acknowledged intents are dropped and the rest are
 * replayed over the new truth. If the result differs from what was on screen,
 * the difference is absorbed over a few frames rather than snapped, so a
 * one-unit rounding disagreement never looks like a stutter.
 *
 * ## What it must never do
 *
 * Predict the opponent. Grant damage, health, a shot, an ending or a reward.
 * Modify the authoritative snapshot to match itself. The room's frame is the
 * truth and this is a guess about what the truth will be; when they differ, the
 * guess loses - visibly if it has to.
 */

/** One intent, as it was sent, kept until the room acknowledges it. */
interface PendingIntent {
  readonly seq: number;
  readonly mx: number;
  readonly my: number;
}

export interface PvpPredictedPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * How much VISUAL DEBT is absorbed gently, and how much is absorbed quickly.
 *
 * A ship travels `PVP_SHIP_SPEED_MU_PER_TICK` = 17 000 mu per tick and its
 * radius is 26 000 mu. **8 000** is under half a tick of travel and under a
 * third of a radius: a rounding disagreement or one reordered command, absorbed
 * over a few frames where the player cannot see it. Above it the same
 * convergence runs on a third of the half-life, which is still bounded by
 * {@link PVP_MAX_CORRECTION_SPEED} and still cannot jump.
 *
 * ## There is deliberately no third band
 *
 * There used to be: a `PVP_SNAP_ERROR_MU` of 52 000, past which the drawn ship
 * was teleported onto the target. It was deleted rather than raised, because
 * the quantity it was reading is not a disagreement about where the ship IS -
 * see the class comment on {@link PvpPrediction}. A distance between the
 * presentation and its own target says how far behind the DRAWING has fallen,
 * and a drawing that has fallen behind is caught up by drawing faster, never by
 * jumping. What decides a jump now is {@link PvpPrediction.acceptAuthoritative},
 * from the rules, and no number here can widen it.
 */
export const PVP_SMOOTH_ERROR_MU = 8_000;

/**
 * Fraction of the remaining error absorbed per millisecond, as a half-life.
 *
 * Expressed in TIME rather than per frame: a fraction per frame converges twice
 * as fast at 120 fps as at 60, which is how a camera ends up feeling different
 * on two machines. 60 ms is a little over one tick - long enough that a small
 * correction is never a visible jerk, short enough that the drawn ship is never
 * meaningfully behind the truth.
 */
export const PVP_RECONCILE_HALF_LIFE_MS = 60;

/**
 * Below this, the drawn position is simply set to the target.
 *
 * Exponential convergence approaches but never arrives, so without a floor the
 * last few hundred milli-units are chased forever and the ship sits permanently
 * - if invisibly - behind the truth. 500 mu is half a world unit, which is
 * under one pixel at every zoom this game allows: the step that closes it
 * cannot be seen, and leaving it open cannot be seen either. Closing it is the
 * one that terminates.
 */
export const PVP_RECONCILE_SETTLE_MU = 500;

/**
 * The room's tick, which is also the window a sub-tick step is spread over.
 *
 * Not a second balance number: it is the cadence the arena already publishes,
 * and it is here so the drawn ship advances on EVERY rendered frame rather than
 * once per command. Without it the prediction is quantised to the 20 Hz intent
 * cadence and the player sees the same stutter the room was blamed for -
 * measured in the browser: the predicted point held still for three rendered
 * frames and then moved a whole tick at once.
 */
export const PVP_PREDICTION_TICK_MS = 50;

/**
 * The most real time one rendered frame may integrate.
 *
 * Exactly the room's own bound, imported rather than restated. A pump that
 * arrives late credits at most {@link PVP_MAX_CATCHUP_MS} of simulated time, so
 * a client that integrated more than that after a stall would be predicting
 * motion the room will never agree happened - and the disagreement would be
 * charged to the correction as a jump.
 *
 * The two clamps have to be the same number for the same reason the movement
 * function is shared: prediction is only prediction if it is the same
 * arithmetic.
 */
export const PVP_MAX_FRAME_MS = PVP_MAX_CATCHUP_MS;

/**
 * The fastest a CORRECTION may move the drawn ship, as a multiple of the ship's
 * own speed.
 *
 * ## The defect this exists for
 *
 * Both sides of the comparison are quantised to WHOLE ticks: the target is the
 * room's position plus one `pvpAdvanceShip` per unacknowledged intent, and the
 * base is the client's own integration in whole ticks. They advance at the same
 * rate - 20 Hz each - but their tick boundaries are not in phase, so the count
 * differs by exactly one whenever an ack lands mid-frame. The target therefore
 * STEPS by a full 17 000 mu, which is over {@link PVP_SMOOTH_ERROR_MU}, so the
 * fast half-life applied about two thirds of it in a single frame.
 *
 * Measured with no browser and no load, driving a room, a wire and a renderer
 * deterministically (`prediction.rate.test.ts`): at 30 fps the drawn ship moved
 * 22.46 units in a 33.4 ms frame against a median of 10.6 - **2.1x** - and at
 * 144 fps the worst frame reached **3.04x** the ship's own top speed. At 60 fps
 * the ratio was exactly 1.000, because three frames close one tick and the
 * phase never slides. An FPS-dependent stutter is the signature of a
 * presentation rule, not of a network.
 *
 * ## The rule
 *
 * A correction may close the gap at up to the speed the ship itself travels, so
 * the drawn ship never exceeds twice its own speed: once for moving, once for
 * catching up. "Its own speed" is the one in force -
 * `pvpTickTravelMu(this.speedBps)` - so a hull under Speed catches up at the
 * rate it is actually moving rather than at the unmodified hull's.
 *
 * Nothing is hidden: the whole debt is still absorbed, still counted, and a
 * discontinuity the room states is still immediate and still named. What
 * changes is that the catching-up is spread over the frames it physically needs
 * instead of being spent in one.
 */
export const PVP_MAX_CORRECTION_SPEED = 1;

/**
 * How much of a frame's own travel a correction may spend pulling BACKWARDS.
 *
 * {@link PVP_MAX_CORRECTION_SPEED} bounds the drawn ship at twice its own speed
 * in the direction it is going. The same cap in the OPPOSITE direction has a
 * different meaning: one times forward plus one times backward is a hull that
 * stands still for a frame while the player holds a key, which
 * `telemetry.spec.ts` counts as `congelados` and refuses at zero.
 *
 * ## Why three quarters, measured rather than chosen
 *
 * Swept on the deterministic wire in `prediction.rate.test.ts` - frame
 * intervals from 6.9 ms to 40 ms, latencies of 0 to 100 ms, frame-time jitter
 * to 30 ms, with and without dropped authoritative frames:
 *
 * | share | combinations that drew a frozen frame |
 * | ----- | ------------------------------------- |
 * | 1.00  | 5                                     |
 * | 0.98  | 0                                     |
 * | 0.75  | 0                                     |
 * | 0.50  | 0                                     |
 *
 * Anything below one removes the stall, so the value is decided at the other
 * end. A correction that opposes the key is the COMMON case - a client that
 * predicts is a client that is ahead - so lowering this slows every catch-up,
 * and a catch-up that finishes late is how a disagreement grows into a snap.
 * Three quarters leaves a quarter of the frame's travel visible (2.8 px at
 * 30 fps, which is movement rather than a stall) and costs a third more frames
 * than the unbounded rule instead of the double a half would cost.
 *
 * It is a presentation constant: nothing about where the room says the ship is
 * changes, only how quickly the drawing is allowed to disagree with itself.
 */
export const PVP_OPPOSING_CORRECTION_SHARE = 0.75;

/**
 * The disagreement a moving ship is ALLOWED to have, because it carries no
 * information.
 *
 * Both sides of the comparison count whole ticks: the room advances one per
 * 50 ms of its clock, the client integrates one per 50 ms of its own. Same rate,
 * different phase - so at any instant the two counts differ by up to exactly
 * one, and one tick of travel is the resolution at which the question "where is
 * the ship" can be answered at all. Demanding better is demanding a number
 * neither side has: the client cannot see the room's sub-tick phase.
 *
 * Correcting inside that band is what produced BOTH symptoms the telemetry
 * caught. When the phase error pointed forwards the drawn ship lurched - 22.5
 * units in a 33 ms frame against a median of 11. When it pointed backwards the
 * correction cancelled the integration exactly and the ship stood still for a
 * frame, which the same file counts as `congelados`. A ship that alternates
 * between double speed and frozen is the stutter, and neither half of it was a
 * position error.
 *
 * Outside the band the correction still runs, still at the capped rate, and a
 * discontinuity the room states is still immediate. What is given up is
 * agreement finer than the tick both clocks are quantised to - which is under a
 * hull's radius, and which the room itself does not have.
 */
export const PVP_PHASE_TOLERANCE_MU = PVP_SHIP_SPEED_MU_PER_TICK;

/**
 * Why a snap happened, recorded rather than inferred.
 *
 * Every one of these is a DISCONTINUITY the room stated or the rules prove.
 * None of them can be reached by the presentation falling behind, which is the
 * whole of the change: a smoothing budget that ran out is not an event about
 * the world.
 *
 * - `world-replaced` is the room saying "this is a different world now" - a
 *   welcome, or a rebuild from a snapshot up to a second old. Drawing the new
 *   truth at once is the honest answer, and it is not a defect.
 * - `teleport` is the hull spending a charge. A legal discontinuity, named
 *   separately because the player asked for it and is entitled to see it land
 *   instantly.
 * - `extreme-authoritative-error` is the room having moved this hull further
 *   than the rules permit in the ticks that actually elapsed. Under the rules
 *   in force, at the speed the frame itself reported, including the booster's
 *   push. There is no arrangement of frame rates, delivery gaps or dropped
 *   packets that produces one: it is the client and the room disagreeing about
 *   the SIMULATION, which is either a fault or a client that was away.
 *
 * Counting them together is what made a soak report seventeen "teleports" that
 * were mostly match transitions. This is not presentation: nothing here changes
 * a pixel, and telling them apart is the prerequisite for deciding whether
 * anything should.
 */
export type PvpSnapKind = 'world-replaced' | 'teleport' | 'extreme-authoritative-error';

export interface PvpSnapEvent {
  readonly kind: PvpSnapKind;
  /**
   * How far the room moved this hull BEYOND what the rules allow, in
   * milli-units. Zero for a replacement and for a teleport, both of which are
   * discontinuities the room declared rather than distances anybody measured.
   *
   * Deliberately not called an error: see {@link PvpPrediction} for why the two
   * quantities this file used to conflate now have two names.
   */
  readonly disagreementMu: number;
}

/**
 * What the room said, beyond the position itself.
 *
 * An object rather than four positional numbers, because three of the four are
 * optional and a caller that knows none of them must keep working exactly as it
 * did - which is what makes each addition provable on its own.
 */
export interface PvpAuthoritativeObservation {
  /**
   * Este casco está a ser EMPURRADO por uma explosão (gate V28G.J).
   *
   * Enquanto for verdade, o input do jogador não move a nave - a sala ignora-o -
   * e portanto a previsão também não pode movê-la. Ver a nota em
   * {@link PvpPrediction.acceptAuthoritative}.
   *
   * Vem do frame e nunca é deduzido: um cliente que inferisse o bloqueio pela
   * distância percorrida estaria a adivinhar mecânica a partir de duas
   * posições, e adivinharia errado em todos os frames agrupados.
   */
  readonly blocked?: boolean;
  /**
   * How fast the RULES are moving this hull right now, in basis points.
   *
   * ## The defect this closes
   *
   * `pvpAdvanceShip` has taken a speed since Speed existed, and everything here
   * called it without one - so the room advanced the ship 25 500 mu per tick
   * while the prediction replayed 17 000. That is not a rounding disagreement,
   * it is a third of the movement, and it accumulated at 8 500 mu per tick for
   * as long as the rune lasted.
   *
   * Traced in the browser, frame by frame: the standing shortfall sat at about
   * 20 000 mu the whole time the rune was up, and every snap in the run was
   * that shortfall plus one grouped delivery of two ticks:
   *
   *     frame 44  auth 810 000  base 807 064  pred 827 000   debt 19 936
   *     frame 45  auth 861 000  tick +2                      debt 53 936  SNAP
   *
   * Three snaps in fourteen seconds, all extreme, all at 53 900 to 54 900 mu,
   * with no browser pause, no rebuild, no starved buffer and the key held
   * throughout.
   *
   * It is still passed, and it still matters, but it is no longer load-bearing
   * for the SNAP: with the two quantities separated, a client told the wrong
   * speed accrues visual debt and converges out of it. What a wrong speed would
   * now produce is an `extreme-authoritative-error`, because the envelope below
   * is computed from it - which is a defect reporting itself rather than a hull
   * jumping.
   */
  readonly speedBps?: number;
  /**
   * O raio do casco previsto, em `mu` (gate V28G.R).
   *
   * Lido do perfil autoritativo, exatamente como `speedBps` é, e pela razão
   * idêntica: `pvpAdvanceShip` trava contra paredes e pilares por este número, e
   * um cliente que previsse com o raio errado encostaria a nave num sítio onde a
   * sala não a encosta - e seria corrigido para sempre.
   *
   * O valor por omissão é a nave de referência, que é o que todo o chamador que
   * não o passa continua a receber.
   */
  readonly hullRadiusMu?: number;
  /**
   * **Os outros cascos do quadro, que este não atravessa** (gate V28H.K).
   *
   * Pela mesma razão exacta que `hullRadiusMu` está aqui: os obstáculos fixos
   * saíram da arena e as naves tomaram-lhes o lugar, portanto `pvpAdvanceShip`
   * trava contra um casco onde travava contra um pilar. Um cliente que
   * previsse sem eles empurraria a nave para dentro do adversário e seria
   * corrigido no quadro seguinte, todos os quadros, enquanto os dois se
   * tocassem - que é a borracha que este parâmetro existe para não haver.
   *
   * São as posições do quadro AUTORITATIVO, que é exactamente o que a sala usa:
   * lá são as do início do tique, aqui são as do último estado conhecido. Nos
   * dois casos ninguém é expulso de uma posição que já mexeu neste tique.
   *
   * Vazio por omissão, o que mantém todo o chamador que não os passe onde
   * estava.
   */
  readonly blockers?: readonly PvpHullBlocker[];
  /**
   * The room's own tick counter, as the frame carried it.
   *
   * The denominator of every legality question. Without it the client cannot
   * tell three ticks delivered together from a hull that moved three ticks in
   * one - and the room publishes grouped frames as a matter of course, at every
   * frame rate except the two where the client's send cadence happens to divide
   * 50 ms exactly. Measured, without a browser, in
   * `packages/game-domain/src/pvp/room-publish-rate.test.ts`.
   */
  readonly tick?: number;
  /**
   * How many teleport charges the hull has left.
   *
   * A DECREASE is a teleport, which is a discontinuity the player asked for.
   * The envelope below would refuse the jump anyway; this only decides which
   * name it is filed under, and a name is what makes a soak report readable.
   *
   * Explicitly `| undefined`: the frame carries effects per seat and a seat
   * that has not been described yet has none, so the caller genuinely has
   * nothing to pass rather than a default to invent.
   */
  readonly teleportCharges?: number | undefined;
}

/**
 * How many recent snaps are kept for inspection.
 *
 * Enough to cover a measurement window without growing during a match that has
 * a bad minute. The counter is unbounded; this list is not.
 */
export const PVP_SNAP_LOG_LIMIT = 64;

/** A hard ceiling on the queue, so a silent room cannot grow it forever. */
export const PVP_MAX_PENDING_INTENTS = 240;

/**
 * ## Two quantities, two names, two jobs
 *
 * This class used to keep one number called `error`, and it decided both how
 * fast to converge AND whether to teleport the hull. Those are not the same
 * measurement, and telling them apart is what closed the last signature
 * `telemetry.spec.ts` kept catching.
 *
 * - **`modelDisagreementMu`** is the AUTHORITY against the RULES. Between two
 *   frames the room advanced `tickGap` ticks; a hull can cover at most
 *   `pvpMaxTravelMu(tickGap, speedBps)` in them, booster included. Anything
 *   further is movement the simulation cannot produce - a teleport, a rebuild,
 *   or a fault - and it is the ONLY thing that may snap the drawing.
 * - **`visualDebtMu`** is the DRAWING against its own target. It is created by
 *   this file's own smoothing: a correction is capped at the ship's speed
 *   (see {@link PVP_MAX_CORRECTION_SPEED}), so a grouped delivery hands the
 *   presentation a gap it then spends a few frames closing. It decides
 *   convergence and nothing else.
 *
 * The old code charged the second to the first. A room publishing two ticks in
 * one frame - which is ordinary, and which happens at every frame rate whose
 * send cadence does not divide 50 ms - handed the drawing 34 000 mu of debt it
 * was correctly closing, and the moment that debt crossed a fixed 52 000 the
 * hull was teleported onto a position it already agreed with. Authority and
 * prediction were identical; the only thing wrong was that the drawing had not
 * finished catching up, and the remedy was to stop it catching up.
 *
 * So: debt converges, disagreement snaps, and no number in this file can turn
 * one into the other.
 */
export class PvpPrediction {
  /** The last authoritative position the room stated for this seat. */
  private authoritative: PvpPredictedPoint | null = null;
  private pending: PendingIntent[] = [];
  /** What is actually on screen, which lags the prediction while correcting. */
  private drawn: PvpPredictedPoint | null = null;
  private lastCorrection: number | null = null;
  /**
   * The integrated position, in WHOLE ticks.
   *
   * What the ship's own movement produces, before any correction and before the
   * sub-tick fraction. Keeping it separate from `drawn` is what stops the
   * fraction accumulating: the visual position is derived from this each frame,
   * never folded back into it.
   */
  private base: PvpPredictedPoint | null = null;
  /** How far into the current tick the integration has got. */
  private advanceMs = 0;
  /** Metrics, for the measurements this change was required to produce. */
  private snaps = 0;
  /** The most recent snaps, with the reason each one happened. */
  private snapLog: PvpSnapEvent[] = [];
  /**
   * How many frames applied a gentle correction rather than a snap.
   *
   * The ordinary case, and the one a measurement has to be able to name: a
   * correction that converges over a few frames is the prediction doing its
   * job, and counting it beside a teleport would make the two look alike.
   */
  private corrections = 0;
  /** The furthest the DRAWING ever fell behind its own target. Never snaps. */
  private worstVisualDebtMu = 0;
  /**
   * The debt as of the most recent frame, which is a different question.
   *
   * A high-water mark cannot tell a transient from a standing shortfall, and
   * those are the two things that had to be told apart: a grouped delivery
   * spikes the debt and it is gone two frames later, while a prediction
   * integrating at the wrong speed sits behind by a constant amount for as long
   * as the rune lasts. Both reach the same peak; only one of them is a defect.
   */
  private visualDebtMu = 0;
  /** The furthest the ROOM ever moved this hull beyond what the rules allow. */
  private worstModelDisagreementMu = 0;
  /** The room's tick as of the last frame adopted, so a gap can be counted. */
  private lastTick: number | null = null;
  /** Teleport charges as of the last frame, so spending one can be recognised. */
  private lastTeleportCharges: number | null = null;
  /** Se este casco está a ser empurrado agora. Dito pela sala (gate V28G.J). */
  private blocked = false;
  /** Frames that carried more than one tick: ordinary, and counted to prove it. */
  private groupedFrames = 0;
  private worstTickGap = 0;
  /** Frames refused as stale or duplicated, so a quiet drop is never silent. */
  private staleFrames = 0;
  /**
   * The speed the ROOM is currently moving this hull at, in basis points.
   *
   * Not a guess and not a local effect: it is whatever the last authoritative
   * frame said, so the replay integrates the same distance the room did. The
   * default is the unmodified hull, which is what every caller that does not
   * pass one keeps getting.
   */
  private speedBps: number = PVP_BASE_SPEED_BPS;

  /** O raio do casco previsto. A nave de referência, até a sala dizer outra. */
  private hullRadiusMu: number = PVP_SHIP_RADIUS_MU;
  /** Os outros cascos, do último quadro autoritativo (gate V28H.K). */
  private blockers: readonly PvpHullBlocker[] = [];

  /** Forgets everything. A new match, a new generation, a closed arena. */
  reset(): void {
    this.authoritative = null;
    this.pending = [];
    this.drawn = null;
    this.lastCorrection = null;
    this.base = null;
    this.advanceMs = 0;
    this.snaps = 0;
    this.snapLog = [];
    this.corrections = 0;
    this.worstVisualDebtMu = 0;
    this.visualDebtMu = 0;
    this.worstModelDisagreementMu = 0;
    this.lastTick = null;
    this.lastTeleportCharges = null;
    this.groupedFrames = 0;
    this.worstTickGap = 0;
    this.staleFrames = 0;
    this.speedBps = PVP_BASE_SPEED_BPS;
    this.hullRadiusMu = PVP_SHIP_RADIUS_MU;
    this.blockers = [];
  }

  /**
   * Records an intent the client has just sent.
   *
   * Kept until the room acknowledges it, because until then the room's latest
   * position does not include it and replaying it is the only way the drawn
   * ship can be where the player expects.
   */
  record(seq: number, intent: PvpIntent): void {
    this.pending.push({ seq, mx: Math.sign(intent.mx), my: Math.sign(intent.my) });
    if (this.pending.length > PVP_MAX_PENDING_INTENTS) {
      /*
       * A room that has stopped acknowledging is a room that has stopped
       * answering, and the reconnection path owns that. Dropping the oldest
       * keeps the queue bounded; the correction that follows will be large and
       * will show up in `snaps`.
       */
      this.pending.shift();
    }
  }

  /**
   * Takes the room's word for where this ship is, and decides whether that word
   * describes the same hull the client has been drawing.
   *
   * ## The three answers, in order
   *
   * 1. `correction` changed: the world was REPLACED rather than advanced - a
   *    welcome, or a rebuild that rewound up to a second - so nothing predicted
   *    describes it any more. Everything pending is dropped and the drawing
   *    snaps: a visible jump, which is honest, rather than a smooth slide to a
   *    place the ship is retroactively discovered to have been in.
   * 2. The room moved this hull further than the rules allow in the ticks that
   *    actually elapsed. That is a DISCONTINUITY - a teleport, or a fault - and
   *    it snaps too, with the amount by which it exceeded the envelope recorded
   *    rather than a threshold it happened to cross.
   * 3. Anything else is ordinary, including a frame that carries three ticks at
   *    once. The room publishes grouped frames as a matter of course, and a
   *    client that read grouping as a jump was reading its own delivery.
   *
   * ## Why a gap is not a distance
   *
   * `pvpMaxTravelMu` is the movement's own arithmetic - the step
   * `pvpAdvanceShip` applies, times the ticks, plus the booster's push, at the
   * speed the frame itself reported. It is not a tolerance and there is nothing
   * in it to widen: it grows when the rules grow and only then. That is what
   * lets a three-tick delivery and a teleport be told apart without either
   * being a number somebody chose.
   */
  acceptAuthoritative(
    point: PvpPredictedPoint,
    ack: number,
    correction: number,
    observed: PvpAuthoritativeObservation = {},
  ): void {
    if (observed.speedBps !== undefined) {
      this.speedBps = observed.speedBps;
    }
    if (observed.hullRadiusMu !== undefined) {
      this.hullRadiusMu = observed.hullRadiusMu;
    }
    if (observed.blockers !== undefined) {
      this.blockers = observed.blockers;
    }
    const replaced = this.lastCorrection !== null && correction !== this.lastCorrection;
    this.lastCorrection = correction;

    /*
     * A frame that is not NEWER carries no truth this hull has not already had.
     *
     * A duplicate re-applies an ack that has already been folded in; one that
     * arrives out of order rewinds the hull to a tick the room has left behind,
     * and the next frame drags it forward again - a rubber-band built entirely
     * out of the transport. The tick is the room's own counter, so "newer" is
     * its opinion rather than an arrival time. A replacement is exempt: after a
     * rebuild the tick legitimately goes backwards, and `correction` is how the
     * room says so.
     */
    if (
      !replaced &&
      observed.tick !== undefined &&
      this.lastTick !== null &&
      observed.tick <= this.lastTick
    ) {
      this.staleFrames += 1;
      return;
    }

    const previous = this.authoritative;
    const tickGap =
      observed.tick !== undefined && this.lastTick !== null ? observed.tick - this.lastTick : null;
    if (observed.tick !== undefined) {
      this.lastTick = observed.tick;
    }
    if (tickGap !== null) {
      this.worstTickGap = Math.max(this.worstTickGap, tickGap);
      if (tickGap > 1) {
        this.groupedFrames += 1;
      }
    }

    /*
     * A charge SPENT is a teleport. Gaining one is a pickup and means nothing
     * here; only the decrease is the player asking to be somewhere else.
     */
    const teleported =
      observed.teleportCharges !== undefined &&
      this.lastTeleportCharges !== null &&
      observed.teleportCharges < this.lastTeleportCharges;
    if (observed.teleportCharges !== undefined) {
      this.lastTeleportCharges = observed.teleportCharges;
    }

    this.authoritative = point;
    // Only what the room has already processed. A command it has not seen still
    // has to be replayed, or the ship visibly rubber-bands backwards.
    this.pending = this.pending.filter((intent) => intent.seq > ack);

    /*
     * **O EMPURRÃO: enquanto durar, este casco não é previsto** (gate V28G.J).
     *
     * ## O que a previsão faria sem saber
     *
     * Andaria. O jogador continua a segurar uma direção, e o preditor replica
     * essa direção sobre a posição autoritativa - portanto ele empurraria a nave
     * localmente contra um bloqueio que a sala está a impor, e o frame seguinte
     * puxava-a de volta. Vinte vezes por segundo, durante 350 ms. É exatamente o
     * que o §6 proíbe: "a predição do cliente não pode mover localmente a nave
     * contra o bloqueio e depois corrigir aos saltos".
     *
     * ## O que faz sabendo
     *
     * Deixa de prever. As intenções por confirmar são deitadas fora - a sala
     * ignora-as, portanto reproduzi-las é literalmente andar contra o bloqueio -
     * e `predict()` passa a devolver a posição autoritativa tal como ela é. O
     * suavizador existente leva a nave desenhada até lá continuamente, sem saltos
     * e sem uma segunda física.
     *
     * As intenções continuam a ser ENVIADAS. Bloquear o teclado aqui seria este
     * cliente a decidir o que a sala aceita, e a sala é que decide.
     */
    this.blocked = observed.blocked === true;
    if (this.blocked) {
      this.pending = [];
    }

    if (replaced || this.drawn === null) {
      this.pending = replaced ? [] : this.pending;
      this.drawn = this.predict();
      // The integration restarts from the new truth: continuing from the old
      // one would re-apply the same correction on every frame that follows.
      this.base = this.drawn;
      this.advanceMs = 0;
      if (replaced) {
        this.recordSnap({ kind: 'world-replaced', disagreementMu: 0 });
      }
      return;
    }

    /*
     * The AUTHORITY against the RULES - never against the drawing.
     *
     * Without a tick there is no denominator and therefore no question: a
     * caller that does not pass one gets no legality check at all, which is the
     * previous behaviour exactly and is what keeps this provable on its own.
     */
    const disagreement =
      previous === null || tickGap === null || this.blocked
        ? 0
        : Math.max(
            0,
            Math.hypot(point.x - previous.x, point.y - previous.y) -
              pvpMaxTravelMu(tickGap, this.speedBps),
          );
    this.worstModelDisagreementMu = Math.max(this.worstModelDisagreementMu, disagreement);

    if (!teleported && disagreement <= 0) {
      return;
    }
    /*
     * Re-seated on the new truth, immediately.
     *
     * `pending` survives: the intents the room has not acknowledged are still
     * legitimate movement, and after a teleport they are movement FROM the new
     * position. Dropping them would rubber-band the hull backwards by exactly
     * the latency - which is what the replay exists to prevent.
     */
    this.drawn = this.predict();
    this.base = this.drawn;
    this.advanceMs = 0;
    this.recordSnap({
      kind: teleported ? 'teleport' : 'extreme-authoritative-error',
      disagreementMu: teleported ? 0 : disagreement,
    });
  }

  /**
   * The predicted position: the room's truth with every unacknowledged intent
   * replayed on top.
   */
  predict(): PvpPredictedPoint | null {
    if (!this.authoritative) {
      return null;
    }
    if (this.blocked) {
      /*
       * Bloqueado: a verdade da sala, sem uma única intenção por cima.
       *
       * Não é uma otimização - `pending` já está vazio - é a afirmação, no sítio
       * onde ela é lida, de que um casco empurrado não tem previsão.
       */
      return this.authoritative;
    }
    let point: PvpPredictedPoint = this.authoritative;
    for (const intent of this.pending) {
      point = pvpAdvanceShip(
        point,
        intent.mx,
        intent.my,
        undefined,
        this.speedBps,
        this.hullRadiusMu,
        this.blockers,
      );
    }
    return point;
  }

  /**
   * Advances what is on screen towards the prediction, and returns it.
   *
   * `deltaMs` is real elapsed time, so the convergence is identical at 30, 60
   * and 144 fps. There are two bands and neither of them jumps: absorb gently,
   * or absorb quickly - both bounded by {@link PVP_MAX_CORRECTION_SPEED}. What
   * jumps is decided in {@link PvpPrediction.acceptAuthoritative}, from the
   * rules, and nothing measured here can reach it.
   */
  sample(
    deltaMs: number,
    held: { mx: number; my: number } | null = null,
  ): PvpPredictedPoint | null {
    const target = this.predict();
    if (!target) {
      return null;
    }
    if (!this.drawn) {
      this.drawn = target;
      return this.drawn;
    }

    /*
     * The drawn ship is INTEGRATED from input, and the room only corrects it.
     *
     * This used to be the other way round: the target was recomputed from the
     * authoritative position on every frame and the drawn position chased it.
     * That passes the network's own unevenness straight through to the screen -
     * frames arrive in bursts, so the target jumps in bursts, and the player
     * sees exactly what they reported. Measured in the browser before this
     * change: 11.4% of frames with the camera scroll frozen, a median step of
     * 1.25 px against a maximum of 79.8 px, and six extreme corrections in four
     * seconds of ordinary movement.
     *
     * Now the visual position advances by the held direction over real elapsed
     * time, at the domain's own speed and through the domain's own collision
     * response, so it moves smoothly whatever the network is doing. The
     * authoritative position is still the truth - it is applied below as a
     * correction, absorbed over time rather than jumped to.
     */
    /*
     * Enquanto o empurrão durar, a tecla segurada não integra nada.
     *
     * A nave desenhada converge para a posição autoritativa pelo suavizador que
     * já existe, que é contínuo e limitado - portanto o jogador vê a nave a ser
     * arrastada, e não a lutar contra o arrasto.
     */
    const steering = this.blocked ? null : held;
    if (steering && (steering.mx !== 0 || steering.my !== 0)) {
      this.advanceMs += Math.min(deltaMs, PVP_MAX_FRAME_MS);
      /*
       * Whole ticks through `pvpAdvanceShip`, so a wall or a pillar stops the
       * visual ship exactly where it stops the real one.
       *
       * The BASE holds whole ticks only. The fraction of the next one is added
       * when the visual position is derived, never folded back in - accumulating
       * it would advance the ship a little further every frame and drift away
       * from the room by however long the match lasts.
       */
      while (this.advanceMs >= PVP_PREDICTION_TICK_MS) {
        this.advanceMs -= PVP_PREDICTION_TICK_MS;
        this.base = pvpAdvanceShip(
          this.base ?? this.drawn,
          steering.mx,
          steering.my,
          undefined,
          this.speedBps,
          this.hullRadiusMu,
          this.blockers,
        );
      }
    } else {
      this.advanceMs = 0;
      this.base = this.base ?? this.drawn;
    }

    const base = this.base ?? this.drawn;

    /*
     * The debt is measured BASE against TARGET - whole tick against whole
     * tick.
     *
     * It used to be measured from the visual position, and that was the defect
     * the browser kept showing after the unit tests went green. The visual
     * position legitimately leads the base by up to a whole tick's worth of
     * sub-tick fraction, while the target has no fraction at all - so there was
     * a permanent disagreement of half a tick on average, the gentle correction
     * ran on every single frame, and it pulled the base backwards against its
     * own integration. Measured in the browser: the base walking 372 797 ->
     * 368 194 -> 373 422 -> 361 917 while the ship was moving steadily east,
     * a median scroll step of 0.88 px against a maximum of 96.8 px, and eight
     * extreme corrections in four seconds.
     *
     * Comparing like with like leaves the fraction where it belongs: on the
     * visual position, which is derived from the corrected base afterwards.
     */
    const dx = target.x - base.x;
    const dy = target.y - base.y;
    /*
     * VISUAL DEBT, and nothing else.
     *
     * How far the drawing is behind its own target. It is created here - by the
     * correction cap, by a grouped delivery arriving between two frames - and
     * it is discharged here, and it never leaves this method. Nothing about the
     * world is decided from it, and there is no band it can cross that makes
     * the hull jump: see the class comment, and see
     * {@link PvpPrediction.acceptAuthoritative} for what does decide a jump.
     */
    const visualDebt = Math.hypot(dx, dy);
    this.visualDebtMu = visualDebt;
    this.worstVisualDebtMu = Math.max(this.worstVisualDebtMu, visualDebt);

    /*
     * Everything else is absorbed into the BASE, over time.
     *
     * Correcting the visual position alone would be undone by the next frame's
     * integration, because that starts from the base.
     *
     * The dead zone depends on whether the ship is MOVING. Standing still, the
     * two clocks cannot disagree and half a world unit is the floor - sub-pixel
     * at every zoom, and small enough that chasing it forever is the only thing
     * closing it would achieve. Moving, the two counts of whole ticks differ by
     * up to one by construction, and correcting inside that band is what made
     * the drawn ship lurch and stall. See {@link PVP_PHASE_TOLERANCE_MU}.
     */
    const moving = steering !== null && (steering.mx !== 0 || steering.my !== 0);
    const deadZone = moving ? PVP_PHASE_TOLERANCE_MU : PVP_RECONCILE_SETTLE_MU;
    if (visualDebt > deadZone) {
      const halfLife =
        visualDebt > PVP_SMOOTH_ERROR_MU
          ? PVP_RECONCILE_HALF_LIFE_MS / 3
          : PVP_RECONCILE_HALF_LIFE_MS;
      const factor = 1 - 2 ** (-deltaMs / halfLife);
      /*
       * Rounded, because the base is fed back into `pvpAdvanceShip` and the
       * domain is integer arithmetic throughout - it refuses a fractional
       * operand rather than silently truncating one. A milli-unit is a
       * thousandth of a world unit, so this is invisible at every zoom and
       * keeps the client's integration in exactly the space the room's is.
       */
      /*
       * The part of the DEBT that is OUTSIDE the band, and only that part.
       *
       * ## The frame this exists for
       *
       * A dead zone that switches the correction on at full strength is a
       * discontinuity, and the telemetry caught it. Two consecutive rendered
       * frames, key held, clear lane, no browser pause:
       *
       *     loopFrame 160  scrollX -215.322  predictedX 419 000  authoritativeX 419 000
       *     loopFrame 161  scrollX -215.322  predictedX 436 000  authoritativeX 419 000
       *
       * The prediction advanced a whole tick and the drawn ship did not move at
       * all: the error had crossed `deadZone` by a hair, the correction was
       * therefore allowed to run, and the budget below let it run backwards at
       * exactly the speed the ship travels forwards. One times forward plus one
       * times backward is a hull standing still while a key is held, which is
       * the `congelados` the same telemetry counts. Reproduced on `f2c3759`
       * with none of the team work in the tree, at a rate of roughly one run in
       * seven, on both trees alike.
       *
       * ## Why subtracting the band is the fix
       *
       * The band is not a threshold for "start correcting hard", it is the
       * resolution at which the question can be answered at all - see
       * {@link PVP_PHASE_TOLERANCE_MU}. So the amount that needs correcting is
       * the amount by which the debt EXCEEDS it, and a debt one milli-unit past
       * the band produces a correction of one milli-unit rather than a full
       * frame's travel. The response is now continuous at the boundary.
       *
       * Nothing is hidden and no tolerance grew: the band is the same width, a
       * discontinuity the room states is still immediate and still named, and a
       * large debt is still absorbed at the same capped speed, because
       * subtracting one tick from it changes the step by less than the cap
       * already does.
       *
       * ## And only while MOVING
       *
       * The two bands are not the same idea. The phase band is the resolution
       * of two quantised clocks, so the debt inside it carries no information
       * and subtracting it is subtracting nothing. The settle band is a floor
       * against chasing sub-pixel noise forever, and the debt inside THAT one
       * is real - it is simply too small to be worth another frame. A standing
       * ship therefore still closes its gap completely, which is what
       * `prediction.test.ts` and `prediction.rate.test.ts` have always asked
       * for and still ask for unchanged.
       */
      const outside = moving ? (visualDebt - deadZone) / visualDebt : 1;
      let stepX = dx * outside * factor;
      let stepY = dy * outside * factor;
      /*
       * And never faster than the ship itself moves.
       *
       * Without this the half-life alone decides, and a one-tick phase
       * disagreement - which is not a disagreement about WHERE the ship is, only
       * about which side counted the tick first - was spent in a single frame.
       * See {@link PVP_MAX_CORRECTION_SPEED} for the measurement.
       *
       * The budget is real time against the room's own tick, so it is identical
       * at 30, 60 and 144 fps: a correction takes the same number of
       * MILLISECONDS on every machine rather than the same number of frames.
       *
       * ## Derived from the ACTIVE speed, not from the base one
       *
       * It used to be `PVP_SHIP_SPEED_MU_PER_TICK` flat, and that quietly made
       * the catch-up slower than the thing it was catching. A hull under Speed
       * travels `pvpTickTravelMu(PVP_RUNE_SPEED_BPS)` = 25 500 mu per tick, so
       * a budget of 17 000 could close a gap at two thirds of the rate the gap
       * was being opened by grouped deliveries at that speed - the convergence
       * still terminated, because the target's own motion is matched by the
       * base's own integration, but every catch-up took half as long again as
       * it needed to, and a catch-up that finishes late is how debt accumulates
       * into something visible.
       *
       * Reading it from `this.speedBps` makes the ceiling on the DRAWN ship
       * exactly twice whatever the room is currently moving it at: once for
       * moving, once for catching up. That is the number `prediction.debt.test.ts`
       * and `prediction.grouped.test.ts` both assert against, and it moves only
       * when the rules move.
       */
      const budget =
        (pvpTickTravelMu(this.speedBps) *
          PVP_MAX_CORRECTION_SPEED *
          Math.min(deltaMs, PVP_MAX_FRAME_MS)) /
        PVP_PREDICTION_TICK_MS;
      /*
       * And a correction that pulls AGAINST the key may spend at most half of
       * it.
       *
       * `budget` is exactly the distance the ship's own integration covers in
       * this frame, so a correction allowed to spend all of it backwards can
       * cancel that integration precisely - which is the standing-still frame
       * this file already describes above. Subtracting the phase band removed
       * the case where the error was barely outside it; this removes the rest,
       * where a genuinely larger error still resolves into a backwards step the
       * size of a whole frame's travel.
       *
       * Measured with the deterministic wire in `prediction.rate.test.ts`,
       * sweeping frame intervals from 6.9 ms to 40 ms against latencies of 0 to
       * 100 ms and frame-time jitter up to 30 ms: 35 of the 128 combinations
       * drew at least one frozen frame before, 2 after subtracting the band,
       * and none with this. What a player loses is that a catch-up which
       * opposes their own movement takes two frames instead of one; what they
       * gain is that the hull they are steering never stops while they hold the
       * key.
       */
      const opposing =
        held !== null && stepX * held.mx + stepY * held.my < 0
          ? budget * PVP_OPPOSING_CORRECTION_SHARE
          : budget;
      const stepLength = Math.hypot(stepX, stepY);
      if (stepLength > opposing && stepLength > 0) {
        const scale = opposing / stepLength;
        stepX *= scale;
        stepY *= scale;
      }
      this.corrections += 1;
      this.base = {
        x: Math.round(base.x + stepX),
        y: Math.round(base.y + stepY),
      };
    }

    /*
     * The visual position, derived from the base AFTER it was corrected.
     *
     * The fraction is never stored: it is recomputed from `advanceMs` every
     * frame, so releasing the key leaves the ship exactly where the last whole
     * tick put it rather than a fraction of a step beyond.
     */
    const corrected = this.base ?? base;
    if (held && (held.mx !== 0 || held.my !== 0)) {
      const next = pvpAdvanceShip(
        corrected,
        held.mx,
        held.my,
        undefined,
        this.speedBps,
        this.hullRadiusMu,
        this.blockers,
      );
      const fraction = this.advanceMs / PVP_PREDICTION_TICK_MS;
      this.drawn = {
        x: corrected.x + (next.x - corrected.x) * fraction,
        y: corrected.y + (next.y - corrected.y) * fraction,
      };
    } else {
      this.drawn = corrected;
    }
    return this.drawn;
  }

  private recordSnap(event: PvpSnapEvent): void {
    this.snaps += 1;
    this.snapLog.push(event);
    while (this.snapLog.length > PVP_SNAP_LOG_LIMIT) {
      this.snapLog.shift();
    }
  }

  /**
   * What the measurements need, and nothing a screen could show.
   *
   * The two distances are reported SEPARATELY and deliberately have no combined
   * form. A single `worstErrorMu` is what let a soak say "the prediction was
   * 54 000 out" when the prediction and the room agreed exactly and only the
   * drawing was behind - and it is what a fix then went looking for in the
   * wrong half of the system.
   */
  metrics(): {
    pending: number;
    snaps: number;
    /** The furthest the DRAWING fell behind its own target. Converges; never snaps. */
    worstVisualDebtMu: number;
    /** The same distance as of the last frame: a transient, or a standing shortfall. */
    visualDebtMu: number;
    /** The furthest the ROOM moved this hull beyond the rules. Snaps; never smoothed. */
    worstModelDisagreementMu: number;
    corrections: number;
    /** Authoritative frames that carried more than one tick. Ordinary. */
    groupedFrames: number;
    worstTickGap: number;
    /** Frames refused as stale or duplicated rather than replayed backwards. */
    staleFrames: number;
    snapLog: readonly PvpSnapEvent[];
  } {
    return {
      pending: this.pending.length,
      snaps: this.snaps,
      worstVisualDebtMu: this.worstVisualDebtMu,
      visualDebtMu: this.visualDebtMu,
      worstModelDisagreementMu: this.worstModelDisagreementMu,
      corrections: this.corrections,
      groupedFrames: this.groupedFrames,
      worstTickGap: this.worstTickGap,
      staleFrames: this.staleFrames,
      snapLog: this.snapLog,
    };
  }
}
